import React from "react";
import { NavLink } from "react-router-dom";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FiShoppingCart } from "react-icons/fi";
import { BsCartCheck } from "react-icons/bs";
import Swal from "sweetalert2";
import Fade from "react-reveal/Fade";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  removeCartItem,
  CartItemType,
} from "../../redux/slice/useCartSlice";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { ApiBaseUrl } from "../../utils/http";
import axios from "axios";
import { Color } from "../../utils/theme";
import { getImage, truncateCaractere, formatCurrency } from "../../utils/Utils";
import Empty from "../../assets/images/appImages/category.png";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { VendeurApikey } from "../../utils/http";
import "./BestSellerCard.css";

type ProductLike = {
  id?: string | number;
  slug?: string;
  nom?: string;
  prix?: number | string;
  stock?: string | number;
  type?: string;
  images?: { image?: string }[];
  variations?: Array<{
    id?: string | number;
    prix?: string | number | null;
    quantite?: string | number;
    stock_management?: boolean | string | number | null;
  }>;
  stock_tracking?: boolean | string | number | null;
  vendeur?: { id?: number } | number;
  new?: boolean;
  categorie?: { promotion?: { is_active?: boolean; taux?: number } };
  sous_categorie?: { promotion?: { is_active?: boolean; taux?: number } };
  promotion?: { is_active?: boolean; taux?: number };
};

function isFalseLike(value: unknown): boolean {
  if (typeof value === "boolean") return value === false;
  if (typeof value === "number") return value === 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "false" || normalized === "0" || normalized === "non";
  }
  return false;
}

function hasAvailableStock(item: ProductLike): boolean {
  const variations = Array.isArray(item?.variations) ? item.variations : [];

  // Stock illimité au niveau produit
  if (isFalseLike(item?.stock_tracking)) {
    return true;
  }

  if (variations.length > 0) {
    return variations.some((variation) => {
      // Stock illimité au niveau variation
      if (isFalseLike(variation?.stock_management)) {
        return true;
      }
      return Number(variation?.quantite ?? 0) > 0;
    });
  }

  return Number(item?.stock ?? 0) > 0;
}

function pickVariation(item: ProductLike) {
  if (!item.variations?.length) return null;
  const withStock = item.variations.find((v) => Number(v.quantite) > 0);
  return withStock ?? item.variations[0];
}

function hasActivePromo(item: ProductLike) {
  return !!(
    item?.categorie?.promotion?.is_active ||
    item?.sous_categorie?.promotion?.is_active ||
    item?.promotion?.is_active
  );
}

function promoRate(item: ProductLike) {
  if (!hasActivePromo(item)) return 0;
  return Number(
    item?.categorie?.promotion?.taux ||
      item?.sous_categorie?.promotion?.taux ||
      item?.promotion?.taux ||
      0
  );
}

function unitPriceForCart(item: ProductLike): number {
  const v = pickVariation(item);
  const raw =
    v?.prix != null && v.prix !== ""
      ? parseFloat(String(v.prix))
      : parseFloat(String(item.prix ?? 0));
  const rate = promoRate(item);
  if (rate > 0) return raw * (1 - rate / 100);
  return raw;
}

function cartItemsMatch(a: CartItemType, b: CartItemType): boolean {
  if (String(a.produit?.id) !== String(b.produit?.id)) return false;
  const aId = a.variations?.[0]?.id;
  const bId = b.variations?.[0]?.id;
  return aId === bId;
}

export type BestSellerCardProps = {
  item: ProductLike;
  onFavoriteToggle?: () => void;
};

export const BestSellerCard = ({ item, onFavoriteToggle }: BestSellerCardProps) => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const devise = vendeur?.devise ?? "XOF";

  const firstVar = item?.variations?.[0] as
    | { images?: { image?: string }[] }
    | undefined;
  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : firstVar?.images?.[0]?.image ?? null;

  const config = { headers: { Authorization: `Bearer ${token}` } };
  const [findBySlug, { data: favoris, isLoading }] = useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) findBySlug(user.slug as string);
  }, [user?.slug, findBySlug]);

  const handleAddFavoris = (id: string | number | undefined) => {
    if (id == null) return;
    const favorie = favoris?.find((x) => x?.produit?.id == id);
    const fd = new FormData();
    fd.append("user", String(user?.id));
    fd.append("produit", String(id));
    const req = favorie
      ? axios.delete(`${ApiBaseUrl}/api/favoris/${favorie?.slug}`, config)
      : axios.post(`${ApiBaseUrl}/api/favoris/`, fd, config);
    req
      .then(() => {
        findBySlug(user?.slug as string);
        onFavoriteToggle?.();
      })
      .catch((err) => console.error(err));
  };

  const isFavorite = (id: string | number | undefined) =>
    !!favoris?.find((x) => x?.produit?.id == id);

  const onToggleFavorite = (e: React.MouseEvent, id: string | number | undefined) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: `Veuilliez vous connecter pour ajouter un produit au favoris .`,
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
      return;
    }
    handleAddFavoris(id);
  };

  const rate = promoRate(item);
  const promo = hasActivePromo(item) && rate > 0;

  const localPriceRange = React.useMemo(() => {
    const candidates: number[] = [];
    const add = (raw: unknown) => {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) candidates.push(n);
    };
    add(item?.prix);
    if (Array.isArray(item?.variations)) {
      item.variations.forEach((variation) => add(variation?.prix));
    }
    if (candidates.length === 0) {
      const fallback = Number(item?.prix);
      const safe = Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
      return { min: safe, max: safe };
    }
    return { min: Math.min(...candidates), max: Math.max(...candidates) };
  }, [item]);

  const promoPriceRange = React.useMemo(() => {
    if (!promo) return localPriceRange;
    const applyPromo = (amount: number) => amount - (amount * rate) / 100;
    return {
      min: applyPromo(localPriceRange.min),
      max: applyPromo(localPriceRange.max),
    };
  }, [localPriceRange, promo, rate]);

  const renderRange = (range: { min: number; max: number }) => {
    if (range.min === range.max) return formatCurrency(range.min, devise);
    return `${formatCurrency(range.min, devise)} - ${formatCurrency(range.max, devise)}`;
  };

  const cartItems = useAppSelector((s) => s.cart?.cartItems ?? []);

  const buildCartPayload = (): CartItemType => {
    const variation = pickVariation(item);
    const variations = variation ? [variation] : [];
    return {
      produit: item as CartItemType["produit"],
      prix: unitPriceForCart(item),
      quantite: 1,
      stock: Number(item.stock ?? 0),
      variations,
      vendeur: typeof item.vendeur === "object" ? item.vendeur?.id ?? 0 : Number(item.vendeur ?? 0),
    };
  };

  const getCartItemForProduct = (): CartItemType | undefined => {
    const candidate = buildCartPayload();
    return cartItems.find((ci) => cartItemsMatch(ci, candidate));
  };

  const inCart = !!getCartItemForProduct();
  const outOfStock = !hasAvailableStock(item);

  const handleToggleCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    const existing = getCartItemForProduct();
    if (existing) {
      dispatch(removeCartItem(existing));
      dispatch(getCartCount());
      dispatch(getTotalAmount());
      Swal.fire({
        icon: "success",
        title: "Produit retiré du panier",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }
    dispatch(addCartProduct(buildCartPayload()));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    Swal.fire({
      icon: "success",
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const nameTrunc =
    typeof window !== "undefined" && window.innerWidth < 720
      ? truncateCaractere(item?.nom, 39)
      : truncateCaractere(item?.nom, 72);

  const cartIconClass = [
    "best-seller-card-cart-icon",
    inCart ? "best-seller-card-cart-icon-in-cart" : "",
    outOfStock ? "best-seller-card-cart-icon-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cartMobileClass = [
    "best-seller-card-cart-icon-mobile",
    inCart ? "best-seller-card-cart-icon-in-cart" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Fade bottom>
      <div className="best-seller-card">
        <div className="best-seller-card-image-wrapper">
          <NavLink
            to={`/produit/${item?.slug}`}
            state={item}
            className="best-seller-card-image-link"
            aria-label={`Voir ${item?.nom}`}
          >
            <img
              loading="lazy"
              src={imgProd ? getImage(imgProd) : Empty}
              alt={item?.nom ?? ""}
              className="best-seller-card-image"
            />
          </NavLink>

          {item?.new === true ? (
            <span className="best-seller-card-badge-right">Nouveau</span>
          ) : null}

          {outOfStock ? (
            <span className="best-seller-card-badge-stock best-seller-card-badge-stock--rupture">
              Rupture
            </span>
          ) : null}

          {promo && !item?.new && !outOfStock ? (
            <span className="best-seller-card-badge-right">-{rate}%</span>
          ) : null}

          <button
            type="button"
            disabled={isLoading || outOfStock}
            className={cartMobileClass}
            aria-label={
              outOfStock
                ? "Produit en rupture de stock"
                : inCart
                  ? "Retirer du panier"
                  : "Ajouter au panier"
            }
            title={
              outOfStock
                ? "Produit en rupture de stock"
                : inCart
                  ? "Retirer du panier"
                  : "Ajouter au panier"
            }
            onClick={handleToggleCart}
          >
            {inCart ? (
              <BsCartCheck className="best-seller-card-favorite-icon" />
            ) : (
              <FiShoppingCart className="best-seller-card-favorite-icon" />
            )}
          </button>

          <button
            type="button"
            disabled={isLoading}
            className="best-seller-card-favorite"
            aria-label="Favoris"
            onClick={(e) => onToggleFavorite(e, item?.id)}
          >
            {isFavorite(item?.id) ? (
              <AiFillHeart className="best-seller-card-favorite-icon-filled" />
            ) : (
              <AiOutlineHeart className="best-seller-card-favorite-icon" />
            )}
          </button>
        </div>

        <div className="best-seller-card-body">
          <NavLink
            to={`/produit/${item?.slug}`}
            state={item}
            className="best-seller-card-name-link"
          >
            <h4 className="best-seller-card-name">{nameTrunc}</h4>
          </NavLink>

          <div className="best-seller-card-price">
            {promo ? (
              <>
                <span
                  className={`best-seller-card-price-final best-seller-card-price-final--promo`}
                >
                  {renderRange(promoPriceRange)}
                </span>
                <span className="best-seller-card-price-original">
                  {renderRange(localPriceRange)}
                </span>
              </>
            ) : (
              <span className="best-seller-card-price-final">
                {renderRange(localPriceRange)}
              </span>
            )}
          </div>

          <div className="best-seller-card-button-wrapper">
            {outOfStock ? (
              <span className="best-seller-card-button best-seller-card-button-disabled">
                Acheter
              </span>
            ) : (
              <NavLink
                to={`/produit/${item?.slug}`}
                state={item}
                className="best-seller-card-button"
              >
                Acheter
              </NavLink>
            )}
            <button
              type="button"
              className={`${cartIconClass} best-seller-card-cart-icon-desktop`}
              onClick={handleToggleCart}
              disabled={outOfStock}
              aria-label={
                outOfStock
                  ? "Produit en rupture de stock"
                  : inCart
                    ? "Retirer du panier"
                    : "Ajouter au panier"
              }
              title={
                outOfStock
                  ? "Produit en rupture de stock"
                  : inCart
                    ? "Retirer du panier"
                    : "Ajouter au panier"
              }
            >
              {inCart ? (
                <BsCartCheck />
              ) : (
                <FiShoppingCart />
              )}
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
};

export default BestSellerCard;
